import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Plus, Briefcase, Edit, Trash2, Upload, FolderPlus, Folder, ChevronDown, ChevronRight, GripVertical, Tag, FileQuestion, Clock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { companyApi } from '../../../lib/api';
import useFabAction from '../../../hooks/useFabAction';

// Category colors
const CATEGORY_COLORS = [
  { name: 'Vert', value: '#22c55e' },
  { name: 'Bleu', value: '#3b82f6' },
  { name: 'Violet', value: '#8b5cf6' },
  { name: 'Rose', value: '#ec4899' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Rouge', value: '#ef4444' },
  { name: 'Jaune', value: '#eab308' },
  { name: 'Cyan', value: '#06b6d4' },
];

export default function ServicesPage() {
  // Services state
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [newService, setNewService] = useState({ 
    name: '', description: '', pricing_mode: 'hourly',
    price_per_hour: '', fixed_price: '', duration_minutes: '',
    category_id: '', image_url: '', tax_credit_eligible: true,
    requires_quote: false
  });

  // Categories state
  const [categories, setCategories] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ 
    name: '', 
    description: '', 
    color: '#22c55e',
    use_custom_hours: false,
    custom_work_hours: [
      { day_of_week: 0, start_time: '08:00', end_time: '18:00', is_active: true },
      { day_of_week: 1, start_time: '08:00', end_time: '18:00', is_active: true },
      { day_of_week: 2, start_time: '08:00', end_time: '18:00', is_active: true },
      { day_of_week: 3, start_time: '08:00', end_time: '18:00', is_active: true },
      { day_of_week: 4, start_time: '08:00', end_time: '18:00', is_active: true },
      { day_of_week: 5, start_time: '08:00', end_time: '12:00', is_active: false },
      { day_of_week: 6, start_time: '08:00', end_time: '12:00', is_active: false },
    ],
    assigned_collaborator_ids: [],
  });
  const [expandedCategories, setExpandedCategories] = useState({});

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      return `${backendUrl}/api${url}`;
    }
    return url;
  };

  const fetchData = async () => {
    try {
      const [servicesRes, categoriesRes, collabsRes] = await Promise.all([
        companyApi.getServices(),
        companyApi.getServiceCategories(),
        companyApi.getCollaborators(),
      ]);
      setServices(servicesRes.data);
      setCategories(categoriesRes.data);
      setCollaborators(collabsRes.data || []);
      
      // Expand all categories by default
      const expanded = {};
      categoriesRes.data.forEach(cat => { expanded[cat.id] = true; });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Group services by category
  const servicesByCategory = useMemo(() => {
    const grouped = { uncategorized: [] };
    categories.forEach(cat => { grouped[cat.id] = []; });
    
    services.forEach(service => {
      if (service.category_id && grouped[service.category_id]) {
        grouped[service.category_id].push(service);
      } else {
        grouped.uncategorized.push(service);
      }
    });
    
    return grouped;
  }, [services, categories]);

  // ============ SERVICE HANDLERS ============
  const resetServiceForm = () => {
    setNewService({ 
      name: '', description: '', pricing_mode: 'hourly',
      price_per_hour: '', fixed_price: '', duration_minutes: '',
      category_id: '', image_url: '', tax_credit_eligible: true,
      requires_quote: false
    });
    setEditingService(null);
  };

  const handleImageUpload = async (e, serviceId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 2 Mo");
      return;
    }

    setIsUploadingImage(true);
    try {
      await companyApi.uploadServiceImage(serviceId, file);
      toast.success('Image uploadée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleCreateService = async () => {
    if (!newService.name) {
      toast.error('Le nom est obligatoire');
      return;
    }
    if (newService.pricing_mode === 'hourly' && !newService.price_per_hour) {
      toast.error('Le tarif horaire est obligatoire');
      return;
    }
    if (newService.pricing_mode === 'fixed' && !newService.fixed_price) {
      toast.error('Le prix fixe est obligatoire');
      return;
    }

    try {
      const data = {
        name: newService.name,
        description: newService.description || null,
        pricing_mode: newService.pricing_mode,
        price_per_hour: newService.pricing_mode === 'hourly' ? parseFloat(newService.price_per_hour) : null,
        fixed_price: newService.pricing_mode === 'fixed' ? parseFloat(newService.fixed_price) : null,
        duration_minutes: newService.duration_minutes ? parseInt(newService.duration_minutes) : null,
        category_id: newService.category_id || null,
        tax_credit_eligible: newService.tax_credit_eligible,
        requires_quote: newService.requires_quote
      };

      if (editingService) {
        await companyApi.updateService(editingService.id, data);
        toast.success('Service mis à jour');
      } else {
        await companyApi.createService(data);
        toast.success('Service créé');
      }
      setIsServiceDialogOpen(false);
      resetServiceForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setNewService({
      name: service.name,
      description: service.description || '',
      pricing_mode: service.pricing_mode || service.price_type || 'hourly',
      price_per_hour: service.price_per_hour?.toString() || '',
      fixed_price: service.fixed_price?.toString() || '',
      duration_minutes: service.duration_minutes?.toString() || '',
      category_id: service.category_id || '',
      image_url: service.image_url || '',
      tax_credit_eligible: service.tax_credit_eligible !== false,
      requires_quote: service.requires_quote || false
    });
    setIsServiceDialogOpen(true);
  };

  useFabAction({ new: () => { resetServiceForm(); setEditingService(null); setIsServiceDialogOpen(true); } });

  const handleDeleteService = async (id) => {
    if (!window.confirm('Supprimer ce service ?')) return;
    try {
      await companyApi.deleteService(id);
      toast.success('Service supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // ============ CATEGORY HANDLERS ============
  const resetCategoryForm = () => {
    setNewCategory({ 
      name: '', 
      description: '', 
      color: '#22c55e',
      use_custom_hours: false,
      custom_work_hours: [
        { day_of_week: 0, start_time: '08:00', end_time: '18:00', is_active: true },
        { day_of_week: 1, start_time: '08:00', end_time: '18:00', is_active: true },
        { day_of_week: 2, start_time: '08:00', end_time: '18:00', is_active: true },
        { day_of_week: 3, start_time: '08:00', end_time: '18:00', is_active: true },
        { day_of_week: 4, start_time: '08:00', end_time: '18:00', is_active: true },
        { day_of_week: 5, start_time: '08:00', end_time: '12:00', is_active: false },
        { day_of_week: 6, start_time: '08:00', end_time: '12:00', is_active: false },
      ],
      assigned_collaborator_ids: [],
    });
    setEditingCategory(null);
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name) {
      toast.error('Le nom de la catégorie est obligatoire');
      return;
    }

    try {
      if (editingCategory) {
        await companyApi.updateServiceCategory(editingCategory.id, newCategory);
        toast.success('Catégorie mise à jour');
      } else {
        await companyApi.createServiceCategory(newCategory);
        toast.success('Catégorie créée');
      }
      setIsCategoryDialogOpen(false);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategory({
      name: category.name,
      description: category.description || '',
      color: category.color || '#22c55e',
      use_custom_hours: category.use_custom_hours || false,
      custom_work_hours: category.custom_work_hours || [
        { day_of_week: 0, start_time: '08:00', end_time: '18:00', is_active: true },
        { day_of_week: 1, start_time: '08:00', end_time: '18:00', is_active: true },
        { day_of_week: 2, start_time: '08:00', end_time: '18:00', is_active: true },
        { day_of_week: 3, start_time: '08:00', end_time: '18:00', is_active: true },
        { day_of_week: 4, start_time: '08:00', end_time: '18:00', is_active: true },
        { day_of_week: 5, start_time: '08:00', end_time: '12:00', is_active: false },
        { day_of_week: 6, start_time: '08:00', end_time: '12:00', is_active: false },
      ],
      assigned_collaborator_ids: category.assigned_collaborator_ids || [],
    });
    setIsCategoryDialogOpen(true);
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ? Les services seront conservés mais n\'auront plus de catégorie.')) return;
    try {
      await companyApi.deleteServiceCategory(id);
      toast.success('Catégorie supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // ============ RENDER SERVICE CARD ============
  const ServiceCard = ({ service }) => (
    <div className="flex items-center gap-3 p-3 bg-background rounded-lg border hover:border-primary/30 transition-colors group">
      {service.image_url ? (
        <img src={getImageUrl(service.image_url)} alt={service.name} className="w-12 h-12 rounded-lg object-cover" />
      ) : (
        <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
          <Briefcase className="w-5 h-5 text-muted-foreground" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate">{service.name}</p>
          {service.requires_quote && (
            <Badge variant="outline" className="text-orange-600 border-orange-300 text-[10px] px-1 shrink-0">
              <FileQuestion className="w-3 h-3 mr-0.5" />
              Devis
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {service.pricing_mode === 'hourly' && service.price_per_hour && (
            <span>{service.price_per_hour}€/h</span>
          )}
          {service.pricing_mode === 'fixed' && service.fixed_price && (
            <span>{service.fixed_price}€ forfait</span>
          )}
          {service.pricing_mode === 'info_only' && (
            <span className="text-blue-600">Informatif</span>
          )}
          {service.tax_credit_eligible && (
            <Badge variant="outline" className="text-green-600 border-green-200 text-[10px] px-1">-50%</Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <label className="cursor-pointer">
          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, service.id)} />
          <div className="p-2 hover:bg-muted rounded-md" data-testid={`upload-service-${service.id}`}>
            <Upload className="w-4 h-4 text-muted-foreground" />
          </div>
        </label>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8" 
          onClick={(e) => { e.stopPropagation(); handleEditService(service); }}
          data-testid={`edit-service-${service.id}`}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-destructive" 
          onClick={(e) => { e.stopPropagation(); handleDeleteService(service.id); }}
          data-testid={`delete-service-${service.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Services</h1>
          <p className="text-muted-foreground mt-1">Organisez vos services par catégories</p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full text-xs"
            onClick={async () => {
              if (!window.confirm("Voulez-vous vérifier automatiquement l'éligibilité au crédit d'impôt ?\n\nLes services contenant des mots-clés comme « élagage », « abattage », « pose de gazon », « aménagement paysager », « création de massifs », « diagnostic » seront marqués comme NON éligibles au crédit d'impôt.\n\nVous pourrez toujours modifier individuellement chaque service ensuite.")) return;
              try {
                const res = await companyApi.autoFixTaxCredit();
                const count = res.data?.updated_count || 0;
                if (count === 0) {
                  toast.info('Aucun service à corriger — votre catalogue est déjà bien classé.');
                } else {
                  toast.success(`${count} service${count > 1 ? 's' : ''} corrigé${count > 1 ? 's' : ''} : ` + (res.data?.updated || []).map(x => x.name).join(', '));
                }
                fetchData();
              } catch (e) {
                toast.error('Erreur lors de la correction automatique');
              }
            }}
            data-testid="auto-fix-tax-credit-btn"
          >
            <Tag className="w-3.5 h-3.5 mr-1.5" />
            Vérifier l&apos;éligibilité crédit d&apos;impôt
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => { resetCategoryForm(); setIsCategoryDialogOpen(true); }}>
            <FolderPlus className="w-4 h-4 mr-2" />
            Catégorie
          </Button>
          <Button className="rounded-full" onClick={() => { resetServiceForm(); setIsServiceDialogOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Service
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{services.length}</p>
              <p className="text-xs text-muted-foreground">Services actifs</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Folder className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{categories.length}</p>
              <p className="text-xs text-muted-foreground">Catégories</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Tag className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{servicesByCategory.uncategorized?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Sans catégorie</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Categories and Services */}
      <div className="space-y-4">
        {/* Categorized services */}
        {categories.map(category => (
          <Card key={category.id} className="overflow-hidden">
            <div 
              className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => toggleCategory(category.id)}
            >
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color || '#22c55e' }} />
              {expandedCategories[category.id] ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
              <div className="flex-1">
                <p className="font-medium">{category.name}</p>
                {category.description && (
                  <p className="text-xs text-muted-foreground">{category.description}</p>
                )}
              </div>
              <Badge variant="secondary">{servicesByCategory[category.id]?.length || 0} services</Badge>
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditCategory(category)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteCategory(category.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {expandedCategories[category.id] && servicesByCategory[category.id]?.length > 0 && (
              <CardContent className="pt-0 pb-4 px-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 ml-7">
                  {servicesByCategory[category.id].map(service => (
                    <ServiceCard key={service.id} service={service} />
                  ))}
                </div>
              </CardContent>
            )}
            
            {expandedCategories[category.id] && servicesByCategory[category.id]?.length === 0 && (
              <CardContent className="pt-0 pb-4 px-4">
                <p className="text-sm text-muted-foreground ml-7">Aucun service dans cette catégorie</p>
              </CardContent>
            )}
          </Card>
        ))}

        {/* Uncategorized services */}
        {servicesByCategory.uncategorized?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-400" />
                Sans catégorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {servicesByCategory.uncategorized.map(service => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {services.length === 0 && !isLoading && (
          <Card>
            <CardContent className="p-8 text-center">
              <Briefcase className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">Aucun service configuré</p>
              <Button className="mt-4 rounded-full" onClick={() => { resetServiceForm(); setIsServiceDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Créer mon premier service
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Service Dialog */}
      <Dialog open={isServiceDialogOpen} onOpenChange={(open) => { setIsServiceDialogOpen(open); if (!open) resetServiceForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingService ? 'Modifier le service' : 'Nouveau Service'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nom du service *</Label>
              <Input data-testid="service-name" value={newService.name}
                onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                placeholder="Tonte de pelouse" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea data-testid="service-desc" value={newService.description}
                onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                placeholder="Description du service..." />
            </div>
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={newService.category_id || 'none'} onValueChange={(value) => setNewService({ ...newService, category_id: value === 'none' ? '' : value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sans catégorie</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Type de tarification *</Label>
              <Select value={newService.pricing_mode}
                onValueChange={(value) => setNewService({ ...newService, pricing_mode: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Taux horaire (€/h)</SelectItem>
                  <SelectItem value="fixed">Prix fixe (forfait)</SelectItem>
                  <SelectItem value="info_only">Informatif (sans prix)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newService.pricing_mode === 'hourly' && (
              <div className="space-y-2">
                <Label>Tarif horaire (€/h) *</Label>
                <Input type="number" value={newService.price_per_hour}
                  onChange={(e) => setNewService({ ...newService, price_per_hour: e.target.value })}
                  placeholder="35" />
              </div>
            )}
            {newService.pricing_mode === 'fixed' && (
              <>
                <div className="space-y-2">
                  <Label>Prix fixe (€) *</Label>
                  <Input type="number" value={newService.fixed_price}
                    onChange={(e) => setNewService({ ...newService, fixed_price: e.target.value })}
                    placeholder="150" />
                </div>
                <div className="space-y-2">
                  <Label>Durée estimée (minutes)</Label>
                  <Input type="number" value={newService.duration_minutes}
                    onChange={(e) => setNewService({ ...newService, duration_minutes: e.target.value })}
                    placeholder="120" />
                </div>
              </>
            )}
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <Label>Crédit d'impôt (-50%)</Label>
                <p className="text-xs text-muted-foreground">Service éligible à l'avance immédiate</p>
              </div>
              <Switch checked={newService.tax_credit_eligible}
                onCheckedChange={(checked) => setNewService({ ...newService, tax_credit_eligible: checked })} />
            </div>
            <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div>
                <Label className="flex items-center gap-2">
                  <FileQuestion className="w-4 h-4 text-orange-600" />
                  Sur devis uniquement
                </Label>
                <p className="text-xs text-muted-foreground">Ce service n'apparaîtra que dans les widgets RDV</p>
              </div>
              <Switch 
                checked={newService.requires_quote}
                onCheckedChange={(checked) => setNewService({ ...newService, requires_quote: checked })} 
                data-testid="requires-quote-toggle"
              />
            </div>
            <Button className="w-full rounded-full" onClick={handleCreateService}>
              {editingService ? 'Mettre à jour' : 'Créer le service'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={isCategoryDialogOpen} onOpenChange={(open) => { setIsCategoryDialogOpen(open); if (!open) resetCategoryForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCategory ? 'Modifier la catégorie' : 'Nouvelle Catégorie'}
            </DialogTitle>
            <DialogDescription>
              Les catégories permettent d'organiser vos services et de les sélectionner facilement dans les widgets.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nom de la catégorie *</Label>
              <Input value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="Ex: Entretien jardin" />
            </div>
            <div className="space-y-2">
              <Label>Description (optionnel)</Label>
              <Textarea value={newCategory.description}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                placeholder="Description de la catégorie..." />
            </div>
            <div className="space-y-2">
              <Label>Couleur</Label>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_COLORS.map(color => (
                  <button
                    key={color.value}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${newCategory.color === color.value ? 'border-primary scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setNewCategory({ ...newCategory, color: color.value })}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
            
            {/* Horaires personnalisés */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Horaires spécifiques
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Définir des horaires différents pour cette catégorie
                  </p>
                </div>
                <Switch
                  checked={newCategory.use_custom_hours}
                  onCheckedChange={(checked) => setNewCategory({ ...newCategory, use_custom_hours: checked })}
                />
              </div>
              
              {newCategory.use_custom_hours && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((dayName, index) => {
                    const dayHours = newCategory.custom_work_hours?.find(h => h.day_of_week === index) || {
                      day_of_week: index,
                      start_time: '08:00',
                      end_time: '18:00',
                      is_active: index < 5
                    };
                    
                    return (
                      <div key={index} className={`flex items-center gap-2 p-2 rounded ${dayHours.is_active ? 'bg-primary/5' : 'bg-muted/30'}`}>
                        <Switch
                          checked={dayHours.is_active}
                          onCheckedChange={(checked) => {
                            const newHours = newCategory.custom_work_hours.map(h => 
                              h.day_of_week === index ? { ...h, is_active: checked } : h
                            );
                            setNewCategory({ ...newCategory, custom_work_hours: newHours });
                          }}
                        />
                        <span className={`w-10 text-sm ${!dayHours.is_active ? 'text-muted-foreground' : ''}`}>
                          {dayName}
                        </span>
                        {dayHours.is_active ? (
                          <>
                            <Input
                              type="time"
                              value={dayHours.start_time}
                              onChange={(e) => {
                                const newHours = newCategory.custom_work_hours.map(h => 
                                  h.day_of_week === index ? { ...h, start_time: e.target.value } : h
                                );
                                setNewCategory({ ...newCategory, custom_work_hours: newHours });
                              }}
                              className="w-24 h-8 text-sm"
                            />
                            <span className="text-xs text-muted-foreground">à</span>
                            <Input
                              type="time"
                              value={dayHours.end_time}
                              onChange={(e) => {
                                const newHours = newCategory.custom_work_hours.map(h => 
                                  h.day_of_week === index ? { ...h, end_time: e.target.value } : h
                                );
                                setNewCategory({ ...newCategory, custom_work_hours: newHours });
                              }}
                              className="w-24 h-8 text-sm"
                            />
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">Fermé</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Collaborateurs assignés à la catégorie */}
            <div className="space-y-2 pt-2 border-t">
              <Label>Collaborateurs assignés (priorité sur les widgets)</Label>
              <p className="text-xs text-muted-foreground">
                Si défini, seuls ces collaborateurs/commerciaux pourront recevoir les RDV/réservations pour cette catégorie. Sinon, on utilise les collaborateurs du widget.
              </p>
              <div className="max-h-44 overflow-y-auto border rounded-lg p-2 space-y-1">
                {collaborators.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">Aucun collaborateur</p>
                ) : (
                  collaborators.map((collab) => {
                    const isChecked = (newCategory.assigned_collaborator_ids || []).includes(collab.id);
                    return (
                      <div
                        key={collab.id}
                        data-testid={`category-collab-row-${collab.id}`}
                        onClick={() => {
                          const cur = newCategory.assigned_collaborator_ids || [];
                          const next = isChecked ? cur.filter(id => id !== collab.id) : [...cur, collab.id];
                          setNewCategory({ ...newCategory, assigned_collaborator_ids: next });
                        }}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          isChecked ? 'bg-primary/10 border border-primary' : 'hover:bg-muted/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          readOnly
                          checked={isChecked}
                          className="pointer-events-none"
                        />
                        <span className="text-sm flex-1">{collab.name}</span>
                        {collab.role === 'commercial' && (
                          <span className="text-[10px] uppercase font-semibold text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                            Commercial
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              {(newCategory.assigned_collaborator_ids || []).length === 0 && (
                <p className="text-xs text-muted-foreground italic">
                  Aucun collaborateur sélectionné — la catégorie héritera des collaborateurs du widget.
                </p>
              )}
            </div>
            
            <Button className="w-full rounded-full" onClick={handleCreateCategory}>
              {editingCategory ? 'Mettre à jour' : 'Créer la catégorie'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
